/**
 * Bringing a list across from the system the firm is leaving.
 *
 * ---------------------------------------------------------------------------
 * THE ONE THING THIS SCREEN IS FOR
 * ---------------------------------------------------------------------------
 *
 * Checking the file before it is imported, and showing everything wrong with it at
 * once. The server never writes a partial import — one bad row stops the file — so the
 * only way this feature is usable is if the person can see all twelve problems, fix
 * them in Excel in one pass, and come back.
 *
 * Hence two steps, always, with no way to skip the first. The dry run validates the
 * whole file and writes nothing; only then does the Import button appear. A single
 * button that either worked or produced a wall of red would be the same code and a much
 * worse experience.
 *
 * Row numbers count the header as row 1, matching the margin in Excel. That is the
 * difference between "row 47" being actionable and being a puzzle.
 *
 * Shared by leads and clients because the two differ only in their columns, and the
 * columns come from the server — including the fields this particular firm added for
 * itself, which appear here without any change to this file.
 */

import {
  ApiError,
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type ImportOutcome,
  type ImportRowProblem,
  type ImportTemplate,
} from '@legalcrm/api';
import {
  Badge,
  Button,
  Modal,
  Spinner,
  Table,
  useToast,
  type Column,
} from '@legalcrm/ui';
import { useState } from 'react';

export type ImportKind = 'leads' | 'clients';

const KINDS: Record<
  ImportKind,
  { noun: string; plural: string; endpoint: string; template: string; invalidates: string }
> = {
  leads: {
    noun: 'lead',
    plural: 'leads',
    endpoint: STAFF.leadImport,
    template: STAFF.leadImportTemplate,
    invalidates: 'leads',
  },
  clients: {
    noun: 'client',
    plural: 'clients',
    endpoint: STAFF.clientImport,
    template: STAFF.clientImportTemplate,
    invalidates: 'clients',
  },
};

export function ImportCsvDialog({
  kind,
  open,
  onClose,
}: {
  kind: ImportKind;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const { toast } = useToast();
  const config = KINDS[kind];

  const [fileName, setFileName] = useState('');
  const [content, setContent] = useState('');
  /** Set when the file is an .xlsx: `content` then holds it as base64. */
  const [isWorkbook, setIsWorkbook] = useState(false);
  const [checked, setChecked] = useState<ImportOutcome | null>(null);
  /** The file itself was unusable — wrong columns, no header. Not a row problem. */
  const [refusal, setRefusal] = useState('');
  const [showColumns, setShowColumns] = useState(false);

  const template = useApiQuery<ImportTemplate>(
    qk.list(`${config.plural}-import-template`),
    config.template,
    { enabled: open },
  );

  function reset() {
    setFileName('');
    setContent('');
    setIsWorkbook(false);
    setChecked(null);
    setRefusal('');
  }

  const run = useApiMutation<ImportOutcome, { dryRun: boolean }>(
    {
      mutationFn: ({ dryRun }) =>
        api.post<ImportOutcome>(`${config.endpoint}/`, {
          ...(isWorkbook ? { workbook: content } : { content }),
          dry_run: dryRun,
        }),
      // Only the real run changes anything, but invalidating after a dry run costs one
      // request and removes a class of stale-list bug that is miserable to diagnose.
      invalidates: [qk.all(config.invalidates)],
    },
    {
      onSuccess: (outcome) => {
        setRefusal('');
        setChecked(outcome);
        if (!outcome.dry_run && outcome.ok) {
          toast({
            title: `${outcome.created} ${outcome.created === 1 ? config.noun : config.plural} imported`,
            description: fileName,
            tone: 'success',
          });
          reset();
          onClose();
        }
      },
      onError: (error) => {
        // A 409 with a reason is the file being refused as a whole. Anything else is a
        // failure of the request, and saying so plainly beats showing an empty result.
        setChecked(null);
        // A server that answered is not a connection problem, and telling somebody to
        // check their connection while the server was failing sent them the wrong way.
        setRefusal(
          error instanceof ConflictError
            ? error.detail
            : error instanceof ApiError && error.status > 0
              ? 'Something went wrong on our side while reading this file. Nothing has been created. Try again, and tell us if it keeps happening.'
              : 'The file could not be sent. Check the connection and try again.',
        );
      },
    },
  );

  /**
   * `FileReader`, not `file.text()`.
   *
   * `Blob.text()` is the shorter spelling and is missing from older Safari, which a
   * firm on an office iPad may well be running. `FileReader` is supported everywhere
   * and costs three lines.
   */
  function readFile(file: File) {
    reset();
    setFileName(file.name);
    const reader = new FileReader();
    reader.onerror = () =>
      setRefusal('That file could not be read from disk. Try choosing it again.');

    // An Excel workbook goes to the server as it is, base64 inside the same JSON body,
    // and the server reads its first sheet. Read as text it arrives as binary noise.
    if (/\.xlsx$/i.test(file.name)) {
      reader.onload = () => {
        const url = String(reader.result ?? '');
        setIsWorkbook(true);
        setContent(url.slice(url.indexOf(',') + 1));
      };
      reader.readAsDataURL(file);
      return;
    }

    reader.onload = () => setContent(String(reader.result ?? ''));
    // Read as UTF-8. A file saved as Windows-1252 arrives with its accents mangled
    // here, and the server's decoder cannot help once the browser has decided — so a
    // name with an accent may need re-saving as CSV UTF-8. The alternative is reading
    // bytes and sending base64, which changes the endpoint for a rare case.
    reader.readAsText(file, 'utf-8');
  }

  function downloadTemplate() {
    if (!template.data) return;
    // A header row and nothing else. The person fills it in from their own export;
    // giving them sample rows would get imported by somebody in a hurry.
    const blob = new Blob([`${template.data.header_row}\n`], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${config.plural}-import-template.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  const problems = checked?.errors ?? [];
  const warnings = checked?.warnings ?? [];
  const readyToImport = checked !== null && checked.dry_run && checked.ok;

  // In the dialog's footer, which stays in view while the body scrolls. At the end of
  // the body they were below the fold once the column list was open, and nothing
  // said there was anything further down.
  const actions = (
    <>
      <Button
        variant="ghost"
        onClick={() => {
          reset();
          onClose();
        }}
      >
        Cancel
      </Button>
      <Button
        variant={readyToImport ? 'secondary' : 'primary'}
        disabled={!content}
        loading={run.isPending && !readyToImport}
        onClick={() => void run.mutateAsync({ dryRun: true })}
      >
        {checked ? 'Check again' : 'Check the file'}
      </Button>
      {/* Absent until the check has passed. There is deliberately no way to import a
          file nobody has looked at. */}
      {readyToImport && (
        <Button
          variant="primary"
          loading={run.isPending}
          onClick={() => void run.mutateAsync({ dryRun: false })}
        >
          Import {checked.created}{' '}
          {checked.created === 1 ? config.noun : config.plural}
        </Button>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      size="lg"
      footer={actions}
      title={`Import ${config.plural} from a spreadsheet`}
      description="The file is checked in full before anything is created. Nothing is written unless every row is valid."
    >
      <div className="space-y-4">
        {/* -- 1. the columns ------------------------------------------- */}
        <div className="rounded-md border border-line p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm text-ink">Columns this firm's file may have</p>
              <p className="text-xs text-ink-muted">
                Headings are matched loosely — "Full Name", "full name" and "FULL_NAME"
                all work.
              </p>
            </div>
            <div className="flex gap-2">
              {/* A bordered button with a chevron, not a bare text link: it opens the
                  list of columns, and as plain text nobody took it for something to
                  press. */}
              <Button
                variant="secondary"
                size="sm"
                aria-expanded={showColumns}
                aria-controls="import-columns"
                onClick={() => setShowColumns((shown) => !shown)}
                leadingIcon={
                  <svg
                    className={`h-3.5 w-3.5 transition-transform ${showColumns ? 'rotate-90' : ''}`}
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 4l4 4-4 4" />
                  </svg>
                }
              >
                {showColumns ? 'Hide' : 'Show'} columns
                {template.data ? ` (${template.data.columns.length})` : ''}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={downloadTemplate}
                disabled={!template.data}
              >
                Download a blank file
              </Button>
            </div>
          </div>

          {template.isLoading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-ink-muted">
              <Spinner /> Reading the column list…
            </div>
          )}

          {showColumns && template.data && (
            <dl id="import-columns" className="mt-3 space-y-2 border-t border-line pt-3">
              {template.data.columns.map((column) => (
                <div key={column.name} className="text-sm">
                  <dt className="flex flex-wrap items-center gap-2">
                    <code className="rounded bg-surface-sunken px-1.5 py-0.5 text-xs text-ink">
                      {column.heading ?? column.name}
                    </code>
                    {column.required && <Badge tone="warning">Required</Badge>}
                    {/* The firm's own fields, called out — somebody wondering where a
                        column came from should not have to guess. */}
                    {column.custom && <Badge tone="info">Your field</Badge>}
                  </dt>
                  <dd className="text-xs text-ink-muted">{column.description}</dd>
                </div>
              ))}
              <p className="pt-1 text-xs text-ink-subtle">
                Up to {template.data.max_rows.toLocaleString()} rows. Any heading not on
                this list is refused rather than ignored.
              </p>
            </dl>
          )}
        </div>

        {/* -- 2. the file ---------------------------------------------- */}
        <div>
          <label className="block text-sm font-medium text-ink" htmlFor="import-file">
            The file
          </label>
          <input
            id="import-file"
            type="file"
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="mt-1 block w-full text-sm text-ink-muted file:mr-3 file:rounded-md file:border file:border-line file:bg-surface file:px-3 file:py-1.5 file:text-sm file:text-ink hover:file:bg-surface-sunken"
            onChange={(event) => {
              const file = event.currentTarget.files?.[0];
              if (file) readFile(file);
            }}
          />
          <p className="mt-1 text-xs text-ink-muted">
            An Excel workbook (.xlsx) or a CSV file. From a workbook, the first sheet is
            read. Accents and a byte-order mark are handled.
          </p>
        </div>

        {/* -- 3. what the check found ---------------------------------- */}
        {refusal && (
          <div className="rounded-md border border-danger/25 bg-danger-subtle p-3 text-sm text-danger">
            <p className="font-medium">This file cannot be read</p>
            <p className="mt-1">{refusal}</p>
          </div>
        )}

        {checked && (
          <div
            className={
              checked.ok
                ? 'rounded-md border border-success/25 bg-success-subtle p-3'
                : 'rounded-md border border-danger/25 bg-danger-subtle p-3'
            }
          >
            <p className="text-sm font-medium text-ink">
              {checked.ok
                ? `${checked.total_rows} rows read, no problems. ${checked.created} ${
                    checked.created === 1 ? config.noun : config.plural
                  } will be created.`
                : `${problems.length} ${problems.length === 1 ? 'problem' : 'problems'} in ${checked.total_rows} rows. Nothing has been created.`}
            </p>
            {!checked.ok && (
              <p className="mt-1 text-xs text-ink-muted">
                Fix the file and choose it again. One bad row stops the whole import, so
                that a firm is never left with 487 of 500 records and no way to tell
                which thirteen are missing.
              </p>
            )}
          </div>
        )}

        {problems.length > 0 && <ProblemTable title="Problems" rows={problems} />}

        {warnings.length > 0 && (
          <>
            <ProblemTable title="Worth a look" rows={warnings} />
            <p className="text-xs text-ink-muted">
              These do not stop the import. A firm genuinely does hear from the same
              person twice — but somebody should decide that rather than find out later.
            </p>
          </>
        )}
      </div>

    </Modal>
  );
}

function ProblemTable({ title, rows }: { title: string; rows: ImportRowProblem[] }) {
  const columns: Column<ImportRowProblem>[] = [
    {
      key: 'row',
      header: 'Row',
      width: 'w-16',
      // Counting the header as row 1, so it matches the margin in Excel.
      render: (row) => <span className="tabular-nums text-ink">{row.row}</span>,
    },
    {
      key: 'column',
      header: 'Column',
      width: 'w-40',
      render: (row) => (
        <code className="text-xs text-ink-muted">{row.column}</code>
      ),
    },
    {
      key: 'message',
      header: 'What is wrong',
      render: (row) => <span className="text-ink-muted">{row.message}</span>,
    },
  ];

  return (
    <div>
      <p className="mb-1 text-sm font-medium text-ink">{title}</p>
      <div className="max-h-64 overflow-y-auto">
        <Table
          caption={title}
          columns={columns}
          rows={rows}
          rowKey={(row) => `${row.row}-${row.column}-${row.message}`}
        />
      </div>
    </div>
  );
}
